import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "quickstart-for-adobe-pdf-services-api-java"
    }}>{`Quickstart for Adobe PDF Services API (Java)`}</h1>
    <p>{`To get started using Adobe PDF Services API, let's walk through a simple scenario - taking an input PDF document and exporting it to Microsoft Word. In this guide, we will walk you through the complete process for creating a program that will accomplish this task. `}</p>
    <h2 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h2>
    <p>{`To complete this guide, you will need:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.oracle.com/technetwork/java/javase/downloads/index.html"
        }}>{`Java`}</a>{` - Java 11 or higher is required. `}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://maven.apache.org/install.html"
        }}>{`Maven`}</a></li>
      <li parentName="ul">{`An Adobe ID. If you do not have one, the credential setup will walk you through creating one.`}</li>
      <li parentName="ul">{`A way to edit code. No specific editor is required for this guide.`}</li>
    </ul>
    <h2 {...{
      "id": "step-one-getting-credentials"
    }}>{`Step One: Getting credentials`}</h2>
    <p>{`1) To begin, open your browser to `}<a parentName="p" {...{
        "href": "https://acrobatservices.adobe.com/dc-integration-creation-app-cdn/main.html?api=pdf-services-api"
      }}>{`https://acrobatservices.adobe.com/dc-integration-creation-app-cdn/main.html?api=pdf-services-api`}</a>{`. If you are not already logged in to Adobe.com, you will need to sign in or create a new user. Using a personal email account is recommend and not a federated ID.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "750px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "74.6875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/5530d/shot1.webp 320w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/0c8fb/shot1.webp 640w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/3ea8a/shot1.webp 750w"],
            "sizes": "(max-width: 750px) 100vw, 750px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/dd4a7/shot1.png 320w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/0f09e/shot1.png 640w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/e9985/shot1.png 750w"],
            "sizes": "(max-width: 750px) 100vw, 750px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/e9985/shot1.png",
            "alt": "Sign in",
            "title": "Sign in",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`2) After registering or logging in, you will then be asked to name your new credentials. Use the name, "New Project". `}</p>
    <p>{`3) Change the "Choose language" setting to "Java". `}</p>
    <p>{`4) Also note the checkbox by, "Create personalized code sample." This will include a large set of samples along with your credentials. These can be helpful for learning more later. `}</p>
    <p>{`5) Click the checkbox saying you agree to the developer terms and then click "Create credentials."`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "70%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/40c27068f03a61c6ec7418b0c097e221/5530d/shot2_spc.webp 320w", "/document-services/docs/static/40c27068f03a61c6ec7418b0c097e221/0c8fb/shot2_spc.webp 640w", "/document-services/docs/static/40c27068f03a61c6ec7418b0c097e221/94b1e/shot2_spc.webp 1280w", "/document-services/docs/static/40c27068f03a61c6ec7418b0c097e221/09047/shot2_spc.webp 1876w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/40c27068f03a61c6ec7418b0c097e221/dd4a7/shot2_spc.png 320w", "/document-services/docs/static/40c27068f03a61c6ec7418b0c097e221/0f09e/shot2_spc.png 640w", "/document-services/docs/static/40c27068f03a61c6ec7418b0c097e221/bbbf7/shot2_spc.png 1280w", "/document-services/docs/static/40c27068f03a61c6ec7418b0c097e221/bd320/shot2_spc.png 1876w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/40c27068f03a61c6ec7418b0c097e221/bbbf7/shot2_spc.png",
            "alt": "Project setup",
            "title": "Project setup",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`6) After your credentials are created, they are automatically downloaded:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "50%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/5530d/shot3_spc.webp 320w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/0c8fb/shot3_spc.webp 640w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/94b1e/shot3_spc.webp 1280w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/c5b4b/shot3_spc.webp 1596w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/dd4a7/shot3_spc.png 320w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/0f09e/shot3_spc.png 640w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/bbbf7/shot3_spc.png 1280w", "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/d866c/shot3_spc.png 1596w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/0d88cd5f33a26e9d7e455dae8efcdc67/bbbf7/shot3_spc.png",
            "alt": "alt",
            "title": "alt",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "step-two-setting-up-the-project"
    }}>{`Step Two: Setting up the project`}</h2>
    <p>{`1) In your Downloads folder, find the ZIP file with your credentials: PDFServicesSDK-JavaSamples.zip. If you unzip that archive, you will find a folder of samples and the `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "452px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "67.1875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/ab2017d1b9d12b9ba06277b11d5f9f0b/5530d/shot5_spc.webp 320w", "/document-services/docs/static/ab2017d1b9d12b9ba06277b11d5f9f0b/a0edd/shot5_spc.webp 452w"],
            "sizes": "(max-width: 452px) 100vw, 452px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/ab2017d1b9d12b9ba06277b11d5f9f0b/dd4a7/shot5_spc.png 320w", "/document-services/docs/static/ab2017d1b9d12b9ba06277b11d5f9f0b/80d88/shot5_spc.png 452w"],
            "sizes": "(max-width: 452px) 100vw, 452px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/ab2017d1b9d12b9ba06277b11d5f9f0b/80d88/shot5_spc.png",
            "alt": "alt",
            "title": "alt",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`2) Take the `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` and place it in a new directory.`}</p>
    <p>{`3) In this directory, create a new file named `}<inlineCode parentName="p">{`pom.xml`}</inlineCode>{` and copy the following content:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<?xml version="1.0" encoding="UTF-8"?>

<project xmlns="http://maven.apache.org/POM/4.0.0"
         xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
         xsi:schemaLocation="http://maven.apache.org/POM/4.0.0 http://maven.apache.org/xsd/maven-4.0.0.xsd">
  <modelVersion>4.0.0</modelVersion>

  <groupId>com.adobe.documentservices</groupId>
  <artifactId>pdfservices-sdk-pdf-guide</artifactId>
  <version>1</version>

  <name>PDF Services Java SDK Samples</name>

  <properties>
    <project.build.sourceEncoding>UTF-8</project.build.sourceEncoding>
    <maven.compiler.source>11</maven.compiler.source>
    <maven.compiler.target>11</maven.compiler.target>
    <pdfservices.sdk.version>4.3.0</pdfservices.sdk.version>
  </properties>

  <dependencies>

    <dependency>
      <groupId>com.adobe.documentservices</groupId>
      <artifactId>pdfservices-sdk</artifactId>
      <version>\${pdfservices.sdk.version}</version>
    </dependency>

    <!--        log4j2 dependency to showcase the use of log4j2 with slf4j API-->
    <!-- https://mvnrepository.com/artifact/org.slf4j/slf4j-log4j12 -->
    <dependency>
      <groupId>org.apache.logging.log4j</groupId>
      <artifactId>log4j-slf4j-impl</artifactId>
      <version>2.21.1</version>
    </dependency>
  </dependencies>

  <build>
    <plugins>
      <plugin>
        <groupId>org.apache.maven.plugins</groupId>
        <artifactId>maven-compiler-plugin</artifactId>
        <version>3.8.0</version>
        <configuration>
          <source>\${maven.compiler.source}</source>
          <target>\${maven.compiler.target}</target>
        </configuration>
      </plugin>
      <plugin>
        <groupId>org.codehaus.mojo</groupId>
        <artifactId>exec-maven-plugin</artifactId>
        <version>1.5.0</version>
        <executions>
          <execution>
            <goals>
              <goal>java</goal>
            </goals>
          </execution>
        </executions>
      </plugin>
    </plugins>
  </build>
</project>
`}</code></pre>
    <p>{`This file will define what dependencies we need and how the application will be built. `}</p>
    <p>{`Our application will take a PDF, `}<inlineCode parentName="p">{`Bodea Brochure.pdf`}</inlineCode>{` (downloadable from `}<a href="https://documentcloud.adobe.com/view-sdk-demo/PDFs/Bodea Brochure.pdf">{`here`}</a>{`) and convert it to a Microsoft Word document, `}<inlineCode parentName="p">{`Bodea Brochure.docx`}</inlineCode>{`.`}</p>
    <p>{`4) In your editor, open the directory where you previously copied the credentials, and create a new directory, `}<inlineCode parentName="p">{`src/main/java`}</inlineCode>{`. In that directory, create `}<inlineCode parentName="p">{`ExportPDFToWord.java`}</inlineCode>{`. `}</p>
    <p>{`Now you're ready to begin coding.`}</p>
    <h2 {...{
      "id": "step-three-creating-the-application"
    }}>{`Step Three: Creating the application`}</h2>
    <p>{`1) We'll begin by including our required dependencies:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import com.adobe.pdfservices.operation.PDFServices;
import com.adobe.pdfservices.operation.PDFServicesMediaType;
import com.adobe.pdfservices.operation.PDFServicesResponse;
import com.adobe.pdfservices.operation.auth.Credentials;
import com.adobe.pdfservices.operation.auth.ServicePrincipalCredentials;
import com.adobe.pdfservices.operation.exception.SDKException;
import com.adobe.pdfservices.operation.exception.ServiceApiException;
import com.adobe.pdfservices.operation.exception.ServiceUsageException;
import com.adobe.pdfservices.operation.io.Asset;
import com.adobe.pdfservices.operation.io.StreamAsset;
import com.adobe.pdfservices.operation.pdfjobs.jobs.ExportPDFJob;
import com.adobe.pdfservices.operation.pdfjobs.params.exportpdf.ExportPDFParams;
import com.adobe.pdfservices.operation.pdfjobs.params.exportpdf.ExportPDFTargetFormat;
import com.adobe.pdfservices.operation.pdfjobs.result.ExportPDFResult;
import org.apache.commons.io.IOUtils;
import org.slf4j.Logger;
import org.slf4j.LoggerFactory;

import java.io.File;
import java.io.IOException;
import java.io.InputStream;
import java.io.OutputStream;
import java.nio.file.Files;
import java.nio.file.Paths;
`}</code></pre>
    <p>{`2) Now let's define our main class:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`public class ExportPDFToWord {

    private static final org.slf4j.Logger LOGGER = LoggerFactory.getLogger(ExportPDFToWord.class);

    public static void main(String[] args) {

    }

}
`}</code></pre>
    <p>{`3) Set the environment variables `}<inlineCode parentName="p">{`PDF_SERVICES_CLIENT_ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PDF_SERVICES_CLIENT_SECRET`}</inlineCode>{` by running the following commands and replacing placeholders `}<inlineCode parentName="p">{`YOUR CLIENT ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`YOUR CLIENT SECRET`}</inlineCode>{` with the credentials present in `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Windows:`}</strong></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`set PDF_SERVICES_CLIENT_ID=<YOUR CLIENT ID>`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`set PDF_SERVICES_CLIENT_SECRET=<YOUR CLIENT SECRET>`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`MacOS/Linux:`}</strong></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`export PDF_SERVICES_CLIENT_ID=<YOUR CLIENT ID>`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`export PDF_SERVICES_CLIENT_SECRET=<YOUR CLIENT SECRET>`}</inlineCode></li>
        </ul>
      </li>
    </ul>
    <p>{`4) Next, we can create our credentials and use them:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Initial setup, create credentials instance
Credentials credentials = new ServicePrincipalCredentials(
        System.getenv("PDF_SERVICES_CLIENT_ID"),
        System.getenv("PDF_SERVICES_CLIENT_SECRET"));

// Creates a PDF Services instance
PDFServices pdfServices = new PDFServices(credentials);
`}</code></pre>
    <p>{`5) Now, let's upload the asset:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`Asset asset = pdfServices.upload(inputStream, PDFServicesMediaType.PDF.getMediaType());
`}</code></pre>
    <p>{`6) Now, let's create the parameters and the job:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Create parameters for the job
ExportPDFParams exportPDFParams = ExportPDFParams.exportPDFParamsBuilder(ExportPDFTargetFormat.DOCX).build();

// Creates a new job instance
ExportPDFJob exportPDFJob = new ExportPDFJob(asset, exportPDFParams);
`}</code></pre>
    <p>{`This set of code defines what we're doing (an Export operation), and sets parameter for the Export PDF job. In this example, the only parameter is the export format ,ie, DOCX.`}</p>
    <p>{`7) The next code block submits the job and gets the job result:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Submit the job and get the job result
String location = pdfServices.submit(exportPDFJob);
PDFServicesResponse<ExportPDFResult> pdfServicesResponse = pdfServices.getJobResult(location, ExportPDFResult.class);

// Get content from the resulting asset(s)
Asset resultAsset = pdfServicesResponse.getResult().getAsset();
StreamAsset streamAsset = pdfServices.getContent(resultAsset);
`}</code></pre>
    <p>{`8) The next code block saves the result at the specified location:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Creates an output stream and copy stream asset's content to it
OutputStream outputStream = Files.newOutputStream(new File("./Bodea Brochure.docx").toPath());
IOUtils.copy(streamAsset.getInputStream(), outputStream);
`}</code></pre>
    <p>{`This code runs the Export process and then stores the result Word document to the file system. `}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "21.875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/8013b963cca4604ee1e14ec2c66dabaa/5530d/shot9.webp 320w", "/document-services/docs/static/8013b963cca4604ee1e14ec2c66dabaa/0c8fb/shot9.webp 640w", "/document-services/docs/static/8013b963cca4604ee1e14ec2c66dabaa/94b1e/shot9.webp 1280w", "/document-services/docs/static/8013b963cca4604ee1e14ec2c66dabaa/9002a/shot9.webp 1894w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/8013b963cca4604ee1e14ec2c66dabaa/dd4a7/shot9.png 320w", "/document-services/docs/static/8013b963cca4604ee1e14ec2c66dabaa/0f09e/shot9.png 640w", "/document-services/docs/static/8013b963cca4604ee1e14ec2c66dabaa/bbbf7/shot9.png 1280w", "/document-services/docs/static/8013b963cca4604ee1e14ec2c66dabaa/30d26/shot9.png 1894w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/8013b963cca4604ee1e14ec2c66dabaa/bbbf7/shot9.png",
            "alt": "Example running in the command line",
            "title": "Example running in the command line",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`Here's the complete application (`}<inlineCode parentName="p">{`src/main/java/ExportPDFToWord.java`}</inlineCode>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import com.adobe.pdfservices.operation.PDFServices;
import com.adobe.pdfservices.operation.PDFServicesMediaType;
import com.adobe.pdfservices.operation.PDFServicesResponse;
import com.adobe.pdfservices.operation.auth.Credentials;
import com.adobe.pdfservices.operation.auth.ServicePrincipalCredentials;
import com.adobe.pdfservices.operation.exception.SDKException;
import com.adobe.pdfservices.operation.exception.ServiceApiException;
import com.adobe.pdfservices.operation.exception.ServiceUsageException;
import com.adobe.pdfservices.operation.io.Asset;
import com.adobe.pdfservices.operation.io.StreamAsset;
import com.adobe.pdfservices.operation.pdfjobs.jobs.ExportPDFJob;
import com.adobe.pdfservices.operation.pdfjobs.params.exportpdf.ExportPDFParams;
import com.adobe.pdfservices.operation.pdfjobs.params.exportpdf.ExportPDFTargetFormat;
import com.adobe.pdfservices.operation.pdfjobs.result.ExportPDFResult;
import org.apache.commons.io.IOUtils;
import org.slf4j.Logger;
import org.slf4j.LoggerFactory;

import java.io.File;
import java.io.IOException;
import java.io.InputStream;
import java.io.OutputStream;
import java.nio.file.Files;
import java.nio.file.Paths;

public class ExportPDFToWord {

    private static final Logger LOGGER = LoggerFactory.getLogger(ExportPDFToWord.class);

    public static void main(String[] args) {

        try (InputStream inputStream = Files.newInputStream(new File("src/main/resources/Bodea Brochure.pdf").toPath())) {
            // Initial setup, create credentials instance
            Credentials credentials = new ServicePrincipalCredentials(
                    System.getenv("PDF_SERVICES_CLIENT_ID"),
                    System.getenv("PDF_SERVICES_CLIENT_SECRET"));
          
            // Creates a PDF Services instance
            PDFServices pdfServices = new PDFServices(credentials);
          
            // Creates an asset(s) from source file(s) and upload
            Asset asset = pdfServices.upload(inputStream, PDFServicesMediaType.PDF.getMediaType());
          
            // Create parameters for the job
            ExportPDFParams exportPDFParams = ExportPDFParams.exportPDFParamsBuilder(ExportPDFTargetFormat.DOCX)
                    .build();
          
            // Creates a new job instance
            ExportPDFJob exportPDFJob = new ExportPDFJob(asset, exportPDFParams);
          
            // Submit the job and gets the job result
            String location = pdfServices.submit(exportPDFJob);
            PDFServicesResponse<ExportPDFResult> pdfServicesResponse = pdfServices.getJobResult(location, ExportPDFResult.class);
          
            // Get content from the resulting asset(s)
            Asset resultAsset = pdfServicesResponse.getResult().getAsset();
            StreamAsset streamAsset = pdfServices.getContent(resultAsset);
          
            // Creates an output stream and copy stream asset's content to it
            Files.createDirectories(Paths.get("output/"));
            OutputStream outputStream = Files.newOutputStream(new File("output/Bodea Brochure.docx").toPath());
            LOGGER.info("Saving asset at output/Bodea Brochure.docx");
            IOUtils.copy(streamAsset.getInputStream(), outputStream);
        } catch (ServiceApiException | IOException | SDKException | ServiceUsageException e) {
            LOGGER.error("Exception encountered while executing operation", e);
        }
    }
}
`}</code></pre>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next Steps`}</h2>
    <p>{`Now that you've successfully performed your first operation, `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/document-services/docs/overview/pdf-services-api/"
      }}>{`review the documentation`}</a>{` for many other examples and reach out on our `}<a parentName="p" {...{
        "href": "https://community.adobe.com/t5/document-services-apis/ct-p/ct-Document-Cloud-SDK"
      }}>{`forums`}</a>{` with any questions. Also remember the samples you downloaded while creating your credentials also have many demos.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      